/**
 * AICommandGenerator — Contextual pentest command generation via Ollama
 * Inspired by pcybox-attack: AI-driven command generation per phase
 */
import React, { useState, useCallback } from 'react';
import {
  Terminal, Cpu, Loader2, Copy, CheckCircle, ChevronDown, ChevronUp,
  Zap, Shield, Target, Eye, GitBranch, FileText, AlertTriangle, Play
} from 'lucide-react';
import { toast } from '../utils/toastUtils';

const API_BASE = '/api/v1/ai/commands';

const PHASES = [
  { id: 'recon', label: 'Reconnaissance', icon: '🔍', color: 'blue', description: 'Collecte passive d\'informations' },
  { id: 'scan', label: 'Scan & Enumeration', icon: '📡', color: 'cyan', description: 'Ports, services, OS' },
  { id: 'exploit', label: 'Exploitation', icon: '⚡', color: 'orange', description: 'Vulnerability exploitation' },
  { id: 'post_exploit', label: 'Post-Exploitation', icon: '🎯', color: 'red', description: 'Privilege escalation, persistence' },
  { id: 'report', label: 'Rapport', icon: '📋', color: 'green', description: 'Documentation et preuves' },
];

const RISK_COLORS = {
  low: { bg: 'green-900/20', border: 'green-500/30', text: 'green-400', label: 'Faible' },
  medium: { bg: 'yellow-900/20', border: 'yellow-500/30', text: 'yellow-400', label: 'Moyen' },
  high: { bg: 'orange-900/20', border: 'orange-500/30', text: 'orange-400', label: 'High' },
  critical: { bg: 'red-900/20', border: 'red-500/30', text: 'red-400', label: 'Critique' },
};

const CommandCard = ({ cmd, index }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const risk = RISK_COLORS[cmd.risk_level] || RISK_COLORS.low;

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(cmd.command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  return (
    <div className={`bg-${risk.bg} border border-${risk.border} rounded-lg overflow-hidden`}>
      {/* Command line */}
      <div className="p-3 flex items-start gap-3">
        <span className="text-gray-600 text-xs font-mono pt-1 min-w-[1.5rem] text-right">{index + 1}.</span>
        <div className="flex-1 min-w-0">
          {/* Tool badge */}
          <div className="flex items-center gap-2 mb-1">
            <span className="px-1.5 py-0.5 bg-gray-800 rounded text-[10px] text-gray-400 font-medium uppercase">{cmd.tool}</span>
            {cmd.category && (
              <span className="px-1.5 py-0.5 bg-blue-900/30 border border-blue-500/20 rounded text-[10px] text-blue-400">{cmd.category}</span>
            )}
            <span className={`ml-auto px-1.5 py-0.5 bg-${risk.bg} border border-${risk.border} rounded text-[10px] text-${risk.text}`}>
              {risk.label}
            </span>
          </div>
          {/* Command */}
          <code className="block text-brand-green font-mono text-xs bg-brand-black rounded px-3 py-2 break-all">
            {cmd.command}
          </code>
        </div>
        {/* Copy button */}
        <button
          onClick={copyCommand}
          className="p-1.5 hover:bg-gray-700 rounded transition-colors flex-shrink-0"
          title="Copier la commande"
        >
          {copied
            ? <CheckCircle className="w-4 h-4 text-green-400" />
            : <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
          }
        </button>
      </div>
      {/* Description */}
      {cmd.description && (
        <div className="px-4 pb-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 transition-colors"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {cmd.description}
          </button>
          {expanded && cmd.expected_output && (
            <div className="mt-2 pl-4 border-l border-gray-700">
              <p className="text-xs text-gray-500">Sortie attendue:</p>
              <p className="text-xs text-gray-400 mt-0.5">{cmd.expected_output}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AICommandGenerator = ({ defaultTarget = '', workspaceId = null, scanId = null }) => {
  const [target, setTarget] = useState(defaultTarget);
  const [phase, setPhase] = useState('scan');
  const [context, setContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showContext, setShowContext] = useState(false);

  const generateCommands = useCallback(async () => {
    if (!target.trim()) { toast.error('Entrez une cible'); return; }
    setLoading(true);
    setResult(null);

    try {
      const body = {
        target: target.trim(),
        phase,
        context: context.trim() || null,
        workspace_id: workspaceId || null,
        scan_id: scanId || null,
      };

      const res = await fetch(`${API_BASE}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Erreur ${res.status}`);
      }

      const data = await res.json();
      setResult(data);
      toast.success(`${data.commands_count} commands generated by AI`);
    } catch (e) {
      toast.error(`Erreur: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [target, phase, context, workspaceId, scanId]);

  const copyAll = async () => {
    if (!result?.commands) return;
    const text = result.commands.map((c, i) => `# ${i + 1}. ${c.description}\n${c.command}`).join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('All commands copied');
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const selectedPhase = PHASES.find(p => p.id === phase);

  return (
    <div className="bg-brand-black-light border border-gray-700/50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-green-900/20 to-teal-900/20">
        <div className="flex items-center gap-2 mb-4">
          <Cpu className="w-5 h-5 text-brand-green" />
          <h3 className="text-base font-semibold text-white">AI Command Generator</h3>
          <span className="ml-auto text-xs text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
            Ollama / sylink/sylink2-coder
          </span>
        </div>

        {/* Target input */}
        <div className="space-y-3">
          <input
            type="text"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && generateCommands()}
            placeholder="Cible: 192.168.1.1, example.com, 10.0.0.0/24..."
            className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-green/60"
          />

          {/* Phase selector */}
          <div className="grid grid-cols-5 gap-1">
            {PHASES.map((p) => (
              <button
                key={p.id}
                onClick={() => setPhase(p.id)}
                title={p.description}
                className={`px-2 py-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-1 ${
                  phase === p.id
                    ? 'bg-brand-green/20 border border-brand-green/60 text-brand-green'
                    : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-300'
                }`}
              >
                <span>{p.icon}</span>
                <span className="truncate w-full text-center">{p.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>

          {/* Context toggle */}
          <div>
            <button
              onClick={() => setShowContext(!showContext)}
              className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 transition-colors"
            >
              {showContext ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Additional context (detected services, OS, notes...)
            </button>
            {showContext && (
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Ex: Port 22 (OpenSSH 8.2), 80 (Apache 2.4.41), OS: Ubuntu 20.04..."
                className="w-full mt-2 bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-brand-green/60 h-20 resize-none"
              />
            )}
          </div>

          {/* Generate button */}
          <button
            onClick={generateCommands}
            disabled={loading || !target.trim()}
            className="w-full py-2.5 bg-gradient-to-r from-brand-green/80 to-emerald-600/80 hover:from-brand-green hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-semibold text-black flex items-center justify-center gap-2 transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                AI generating commands...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Generate commands — Phase: {selectedPhase?.icon} {selectedPhase?.label}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="p-4">
          {/* Meta */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-white font-medium">{result.commands_count} commandes</span>
              <span className="text-xs text-gray-500">Phase: {selectedPhase?.icon} {result.phase}</span>
              {result.generated_by === 'fallback' && (
                <span className="text-xs text-yellow-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Commandes de base (IA indisponible)
                </span>
              )}
              {result.from_cache && (
                <span className="text-xs text-blue-400">depuis cache</span>
              )}
            </div>
            <button
              onClick={copyAll}
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1 px-2 py-1 hover:bg-gray-800 rounded transition-colors"
            >
              <Copy className="w-3 h-3" /> Tout copier
            </button>
          </div>

          {/* Commands list */}
          <div className="space-y-2">
            {result.commands.map((cmd, i) => (
              <CommandCard key={i} cmd={cmd} index={i} />
            ))}
          </div>

          {/* Cible + phase info */}
          <div className="mt-4 p-3 bg-brand-black rounded-lg border border-gray-800">
            <p className="text-xs text-gray-500">
              <span className="text-gray-400 font-medium">Cible:</span> {result.target} ·{' '}
              <span className="text-gray-400 font-medium">Phase:</span> {result.phase_description}
            </p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!result && !loading && (
        <div className="p-8 text-center text-gray-500">
          <Terminal className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select a phase and a target to generate contextual pentest commands</p>
          <p className="text-xs mt-1 opacity-60">Powered by Ollama sylink/sylink2:27b local</p>
        </div>
      )}
    </div>
  );
};

export default AICommandGenerator;
